/* eslint-disable no-useless-escape */
"use client";

import { Common } from "@src/styles/Common";
import { useInViewOnce } from "@src/util/useInViewOnce";
import React, { useMemo, useState } from "react";
import styled from "styled-components";

interface OwnProps {
    buildingInfo: Array<{
        area: string;
        etcPurps: string;
        flrNoNm: string;
        mainAtchGbCdNm: string;
    }>;
    totArea: { square: string; pyong: string };
    MAXIMUM?: number;
    progressBackgroundColor?: string;
    isShowTotalArea?: boolean;
    onExpandedChange?: (isExpanded: boolean) => void;
}

const BuildingStatusTable = ({
    buildingInfo,
    totArea,
    MAXIMUM,
    progressBackgroundColor,
    isShowTotalArea = false,
    onExpandedChange,
}: OwnProps) => {
    const MAX_VISIBLE = MAXIMUM ? MAXIMUM : 10;
    const { ref, inView } = useInViewOnce();
    const [expanded, setExpanded] = useState(false);

    const handleToggleExpand = () => {
        const newExpandedState = !expanded;
        setExpanded(newExpandedState);
        onExpandedChange?.(newExpandedState);
    };

    const toNum = (v?: string) => {
        const n = parseFloat(String(v ?? "").replace(/[^0-9.\-]/g, ""));
        return Number.isFinite(n) ? n : 0;
    };

    const buildingTotalArea = useMemo(() => {
        return buildingInfo?.reduce((sum, item) => sum + toNum(item.area), 0);
    }, [buildingInfo]);

    const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

    const totalCount = buildingInfo?.length;
    const visibleList = expanded ? buildingInfo : buildingInfo?.slice(0, MAX_VISIBLE);
    const remaining = Math.max(0, totalCount - MAX_VISIBLE);

    const highestFloor = useMemo(() => {
        if (!buildingInfo || buildingInfo.length === 0) {
            return "";
        }

        const aboveGroundFloors = buildingInfo.filter((floor) => !floor.flrNoNm.includes("B"));

        if (aboveGroundFloors.length === 0) {
            return "";
        }

        const highestFloorObject = aboveGroundFloors.reduce((prev, current) => {
            const prevFloorNum = parseInt(prev.flrNoNm, 10);
            const currentFloorNum = parseInt(current.flrNoNm, 10);

            return currentFloorNum > prevFloorNum ? current : prev;
        });

        return highestFloorObject.flrNoNm;
    }, [buildingInfo]);

    return (
        <Container ref={ref}>
            <Header>
                <Title>
                    <div>이 건물의 규모는</div>
                    <SubTitle>
                        <Highlight>지상 {highestFloor}</Highlight>
                        입니다.
                    </SubTitle>
                </Title>

                {isShowTotalArea && (
                    <Area>
                        <div className="total">연면적</div>
                        <div className="number">
                            {totArea?.square} {`(${totArea?.pyong})`}
                        </div>
                    </Area>
                )}
            </Header>

            <Body>
                {visibleList?.map((item, idx) => {
                    const floorArea = toNum(item.area);
                    const ratio01 = buildingTotalArea > 0 ? clamp01(floorArea / buildingTotalArea) : 0;

                    return (
                        <Floor key={`${item.flrNoNm}-${idx}`}>
                            <div className="title">{item?.flrNoNm}</div>

                            <div className="floorLayout">
                                <ProgressBar>
                                    <FillClip style={{ backgroundColor: progressBackgroundColor ?? Common.colors.grey02 }}>
                                        <Fill
                                            style={{
                                                transform: `scaleX(${inView ? (ratio01 < 0.1 ? ratio01 + 0.14 : ratio01) : 0})`,
                                                transitionDelay: `${idx * 80}ms`,
                                                background: `${
                                                    item.flrNoNm &&
                                                    typeof item.flrNoNm === "string" &&
                                                    item.flrNoNm.trim().toUpperCase().startsWith("B")
                                                        ? "#B1D0FF"
                                                        : Common.colors.aucMarkerColor
                                                }`,
                                            }}
                                        />
                                    </FillClip>
                                    <FloorLabel>
                                        <div className="labelTitle">
                                            <div className="highlight">주</div>
                                            <div className="divider" />
                                            <div className="etcPurps">{item.etcPurps || " "}</div>
                                        </div>
                                    </FloorLabel>
                                </ProgressBar>

                                <div className="area">
                                    <div>{item.area ? item.area : " "}</div>
                                    <div>{`( ${(floorArea / 3.305785).toFixed(2)}평 )`}</div>
                                </div>
                            </div>
                        </Floor>
                    );
                })}
                {totalCount > MAX_VISIBLE && (
                    <MoreWrapper>
                        <MoreButton type="button" onClick={handleToggleExpand} aria-expanded={expanded}>
                            {expanded ? "접기" : `더보기 (${remaining}개)`}
                        </MoreButton>
                    </MoreWrapper>
                )}
            </Body>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px 0 24px 0;
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-weight: 400;
    width: 100%;
    padding-bottom: 12px;
`;

const Title = styled.div`
    font-size: 16px;
    line-height: 24px;
`;
const SubTitle = styled.div`
    display: flex;
    flex-direction: row;
`;
const Highlight = styled.p`
    font-size: 16px;
    line-height: 24px;
    font-weight: 700;

    color: ${Common.colors.aucMarkerColor};
`;

const Area = styled.div`
    display: flex;
    flex-direction: column;
    align-items: end;
    .total {
        font-size: 12px;
        font-weight: 400;
    }
    .number {
        font-size: 14px;
        font-weight: 700;
    }
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Floor = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;

    .title {
        display: flex;
        align-items: center;
        gap: 4px;
        font-size: 14px;
        font-weight: 700;
        color: black;
    }
    .highlight {
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 14px;
        font-weight: 400;
        min-width: fit-content;
        height: fit-content;
        color: black;
    }
    .divider {
        width: 1.2px;
        align-self: stretch;
        margin: 3.4px 0;
        background-color: black;
    }
    .floorLayout {
        display: flex;
        width: 100%;
        justify-content: space-between;
        align-items: center;
    }
    .area {
        min-width: fit-content;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: end;
        font-size: 14px;
        font-weight: 400;
    }
`;

const ProgressBar = styled.div`
    position: relative;
    width: 70%;
    min-width: 70%;
    height: 24px;
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    align-items: center;
`;

const FillClip = styled.div`
    position: absolute;
    inset: 0;
    border-radius: inherit;
    overflow: hidden;
`;

const Fill = styled.div`
    position: absolute;
    inset: 0;
    transform-origin: left center;
    transform: scaleX(0);
    transition: transform 650ms ease;
`;

const FloorLabel = styled.div`
    position: relative;
    z-index: 1;
    padding-left: 8px;
    font-size: 14px;
    font-weight: 400;
    color: ${Common.colors.darkGrey03};
    color: white;
    line-height: 24px;

    .labelTitle {
        display: flex;
        flex-direction: row;
        gap: 4px;
        font-size: 14px;
        font-weight: 400;
        color: black;
    }

    .etcPurps {
        width: 85%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }
`;

const MoreWrapper = styled.div`
    display: flex;
    justify-content: center;
    padding-top: 8px;
`;

const MoreButton = styled.button`
    appearance: none;
    border: 0;
    border-radius: 8px;
    padding: 8px 12px;
    background: ${Common.colors.aucMarkerColor};
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    &:focus-visible {
        outline: 2px solid ${Common.colors.aucMarkerColor};
        outline-offset: 2px;
    }
`;

export default BuildingStatusTable;
